import { el, svg } from "../core/dom";
import { createRequiredMarker } from "../core/required-marker";
import { part } from "../appearance/parts";
import { inlineStyles } from "../styles/inline";
import { DEFAULT_SELECTION_CONTROL } from "../constants";
import {
  groupPurposesByProduct,
  isMultiProduct,
  productPurposeKey,
} from "../core/productMatrix";
import { renderSelectionControl } from "../modal/selection-control";
import { hasBlockingRequiredElement, inlinePurposeRows, isBlockingRequiredElement } from "./selection";
import type { PurposeItemData } from "../api/types";
import type { SelectionControlRefs } from "../types";
import type { InlinePurposeRenderOptions } from "./types";

export type PurposeRenderRefs = {
  container: HTMLElement;
  /** Purpose controls, keyed by the row's state key. */
  purposeControls: Map<string, SelectionControlRefs>;
  /** Data element checkboxes, keyed `${stateKey}-${elementUuid}`. */
  checkboxes: Map<string, HTMLInputElement>;
  collapseButtons: Map<string, HTMLElement>;
  dataElementContainers: Map<string, HTMLElement>;
  chevrons: Map<string, SVGElement>;
  titleElements: Map<string, HTMLElement>;
  descriptionElements: Map<string, HTMLElement>;
  dataElementLabels: Map<string, HTMLElement>;
};

export function renderInlinePurposes(
  purposes: PurposeItemData[],
  options: InlinePurposeRenderOptions
): PurposeRenderRefs {
  const {
    selectedPurposes,
    collapsedPurposes,
    selectedDataElements,
    products,
    productPurposeOrder,
    settings,
    look,
    isMobile,
    getTranslatedText,
    onPurposeToggle,
    onPurposeCollapse,
    onDataElementToggle,
  } = options;

  const purposeControls = new Map<string, SelectionControlRefs>();
  const checkboxes = new Map<string, HTMLInputElement>();
  const collapseButtons = new Map<string, HTMLElement>();
  const dataElementContainers = new Map<string, HTMLElement>();
  const chevrons = new Map<string, SVGElement>();
  const titleElements = new Map<string, HTMLElement>();
  const descriptionElements = new Map<string, HTMLElement>();
  const dataElementLabels = new Map<string, HTMLElement>();

  const styles = look?.styles ?? inlineStyles;
  const responsive = look?.responsive ?? {
    optionTitle: { fontSize: isMobile ? "14px" : "16px" },
    optionDescription: { fontSize: isMobile ? "11px" : "12px" },
    dataElementText: { fontSize: isMobile ? "12px" : "14px" },
  };
  const headingColor = look?.headingColor ?? settings?.headingColor ?? "#101828";
  const textColor = look?.textColor ?? settings?.textColor ?? "#344054";
  const descriptionColor = look?.descriptionColor ?? textColor;
  const chevronColor = look?.chevronColor ?? settings?.headingColor ?? "#323B4B";
  const selectionControl =
    look?.selectionControl ?? settings?.selectionControl ?? DEFAULT_SELECTION_CONTROL;

  const container = el("div", {
    style: { display: "flex", flexDirection: "column", gap: "16px" },
  });

  const renderPurpose = (purpose: PurposeItemData, productUuid?: string): HTMLElement => {
    const stateKey = productPurposeKey(productUuid, purpose.uuid);
    const purposeWrapper = el("div");
    const purposeName = getTranslatedText("purposes.name", purpose.name, purpose.uuid);

    const chevron = svg(
      "svg",
      {
        width: "7",
        height: "12",
        viewBox: "0 0 7 12",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        stroke: chevronColor,
        "stroke-width": "2",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        style: {
          transform: !collapsedPurposes[stateKey] ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease",
        },
        "aria-hidden": "true",
      },
      svg("path", { d: "M1 1L6 6L1 11" })
    );
    chevrons.set(stateKey, chevron);

    const chevronContainer = el(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "5px",
        },
      },
      chevron
    );

    const titleEl = el("h2", {
      ...part("purpose-title"),
      style: {
        ...styles.optionTitle,
        ...responsive.optionTitle,
        color: headingColor,
      },
    });
    // Appended after `textContent`, which replaces every existing child.
    titleEl.textContent = purposeName;
    if (hasBlockingRequiredElement(purpose)) {
      titleEl.appendChild(createRequiredMarker());
    }
    titleElements.set(stateKey, titleEl);

    const descEl = el("h3", {
      style: {
        ...styles.optionDescription,
        ...responsive.optionDescription,
        color: descriptionColor,
      },
    });
    descEl.textContent = getTranslatedText("purposes.description", purpose.description, purpose.uuid);
    descriptionElements.set(stateKey, descEl);

    const textContainer = el("div", { style: styles.optionTextContainer }, titleEl, descEl);

    const collapseBtn = el("div", {
      style: styles.optionLeft,
      role: "button",
      tabIndex: "0",
      "aria-expanded": String(!collapsedPurposes[stateKey]),
      "aria-controls": `purpose-${stateKey}`,
      "aria-label": `${collapsedPurposes[stateKey] ? "Expand" : "Collapse"} ${purposeName} details`,
      onClick: () => onPurposeCollapse(purpose.uuid, productUuid),
      onKeydown: (e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPurposeCollapse(purpose.uuid, productUuid);
        }
      },
    }, chevronContainer, textContainer);
    collapseButtons.set(stateKey, collapseBtn);

    // The console's selection control, as the banner draws it.
    const purposeControl = renderSelectionControl({
      control: selectionControl,
      checked: selectedPurposes[stateKey] || false,
      onSelect: () => onPurposeToggle(purpose.uuid, productUuid),
      label: `Select all data elements for ${purposeName}`,
      groupName: `redacto-inline-purpose-${stateKey}`,
      size: "small",
      language: options.language,
    });
    purposeControls.set(stateKey, purposeControl);

    const optionRow = el(
      "div",
      { ...part("purpose"), style: styles.optionItem },
      collapseBtn,
      purposeControl.element
    );

    const deContainer = el("div", {
      id: `purpose-${stateKey}`,
      style: styles.dataElementsContainer,
    });

    if (purpose.data_elements && purpose.data_elements.length > 0) {
      for (const dataElement of purpose.data_elements) {
        const combinedId = `${stateKey}-${dataElement.uuid}`;
        const elementName = getTranslatedText("data_elements.name", dataElement.name, dataElement.uuid);

        const labelSpan = el("span", {
          style: {
            ...styles.dataElementText,
            ...responsive.dataElementText,
            color: textColor,
          },
        });
        labelSpan.textContent = elementName;
        dataElementLabels.set(combinedId, labelSpan);

        if (isBlockingRequiredElement(dataElement)) {
          labelSpan.appendChild(createRequiredMarker());
        }

        const deCheckbox = el("input", {
          className: "redacto-checkbox-small",
          type: "checkbox",
          checked: selectedDataElements[combinedId] || false,
          "aria-label": `Select ${elementName}${isBlockingRequiredElement(dataElement) ? " (required)" : ""}`,
          onChange: () => onDataElementToggle(dataElement.uuid, purpose.uuid, productUuid),
        }) as HTMLInputElement;
        checkboxes.set(combinedId, deCheckbox);

        const deRow = el(
          "div",
          { ...part("data-element"), style: styles.dataElementItem },
          labelSpan,
          deCheckbox
        );
        deContainer.appendChild(deRow);
      }
    }

    dataElementContainers.set(stateKey, deContainer);

    if (!collapsedPurposes[stateKey] && purpose.data_elements?.length > 0) {
      deContainer.style.display = "flex";
    } else {
      deContainer.style.display = "none";
    }

    purposeWrapper.appendChild(optionRow);
    purposeWrapper.appendChild(deContainer);
    return purposeWrapper;
  };

  if (!isMultiProduct(products)) {
    // In the order the admin saved for the sole product.
    for (const { purpose } of inlinePurposeRows({
      products,
      purposes,
      product_purpose_order: productPurposeOrder,
    })) {
      container.appendChild(renderPurpose(purpose));
    }
  } else {
    for (const group of groupPurposesByProduct(products, purposes, productPurposeOrder)) {
      const heading = el("div", {
        ...part("product"),
        style: { ...styles.productHeading, color: headingColor },
      });
      heading.textContent = getTranslatedText("products.name", group.product.name, group.product.uuid);
      if (group.product.mandatory) {
        heading.appendChild(createRequiredMarker());
      }
      const section = el(
        "div",
        { style: { display: "flex", flexDirection: "column", gap: "16px" } },
        heading
      );
      for (const purpose of group.purposes) {
        section.appendChild(renderPurpose(purpose, group.product.uuid));
      }
      container.appendChild(section);
    }
  }

  return {
    container,
    purposeControls,
    checkboxes,
    collapseButtons,
    dataElementContainers,
    chevrons,
    titleElements,
    descriptionElements,
    dataElementLabels,
  };
}
